import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import * as telemarketingService from '../../services/crm/telemarketingService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const CheckCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const XCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="15" y1="9" x2="9" y2="15"/>
    <line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);

const statusColors: Record<string, string> = {
  'initiated': 'bg-blue-100 text-blue-700',
  'ringing': 'bg-purple-100 text-purple-700',
  'in-progress': 'bg-green-100 text-green-700',
  'completed': 'bg-gray-100 text-gray-700',
  'failed': 'bg-red-100 text-red-700',
  'busy': 'bg-yellow-100 text-yellow-700',
  'no-answer': 'bg-orange-100 text-orange-700'
};

const outcomeColors: Record<string, string> = {
  'interested': 'bg-green-100 text-green-700',
  'not_interested': 'bg-red-100 text-red-700',
  'callback': 'bg-blue-100 text-blue-700',
  'voicemail': 'bg-yellow-100 text-yellow-700',
  'meeting_booked': 'bg-purple-100 text-purple-700',
  'qualified': 'bg-green-100 text-green-700'
};

export default function CampaignCallMonitor() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeOrgId } = useCrmOrgStore();
  const [campaign, setCampaign] = useState<telemarketingService.TelemarketingCampaign | null>(null);
  const [calls, setCalls] = useState<telemarketingService.TelemarketingCall[]>([]);
  const [activeCalls, setActiveCalls] = useState<telemarketingService.TelemarketingCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'active' | 'completed'>('active');

  useEffect(() => {
    if (campaignId && activeOrgId) {
      loadData();
      // Poll for active calls every 5 seconds
      const interval = setInterval(loadActiveCalls, 5000);
      return () => clearInterval(interval);
    } else if (campaignId && !activeOrgId) {
      // activeOrgId not yet loaded, show loading
      setLoading(true);
    }
  }, [campaignId, activeOrgId]);

  // Standalone page - header managed inline

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignData, callsData, activeData] = await Promise.all([
        telemarketingService.getCampaign(campaignId!, activeOrgId!),
        telemarketingService.listCalls(campaignId!, { limit: 50, offset: 0 }),
        telemarketingService.getActiveCalls(campaignId!)
      ]);
      
      setCampaign(campaignData);
      setCalls(callsData.data || []);
      setActiveCalls(activeData);
    } catch (error: any) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load call data');
    } finally {
      setLoading(false);
    }
  };

  const loadActiveCalls = async () => {
    if (!campaignId) return;
    try {
      const activeData = await telemarketingService.getActiveCalls(campaignId);
      setActiveCalls(activeData);
    } catch (error) {
      console.error('Failed to refresh active calls:', error);
    }
  };

  const handleTransfer = async (callSid: string, toNumber: string) => {
    try {
      await telemarketingService.transferCall(callSid, toNumber);
      toast.success('Call transferred');
      loadActiveCalls();
    } catch (error: any) {
      toast.error(error.message || 'Failed to transfer call');
    }
  };

  const handleHangup = async (callSid: string) => {
    try {
      await telemarketingService.hangupCall(callSid);
      toast.success('Call ended');
      loadActiveCalls();
    } catch (error: any) {
      toast.error(error.message || 'Failed to end call');
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Page Header */}
      {campaign && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Call Monitor: {campaign.name}</h1>
          <p className="text-gray-600 mt-1">Real-time call monitoring and history</p>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setTab('active')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              tab === 'active'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active Calls ({activeCalls.length})
          </button>
          <button
            onClick={() => setTab('completed')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              tab === 'completed'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Call History ({calls.length})
          </button>
        </nav>
      </div>

      {/* Active Calls */}
      {tab === 'active' && (
        <div className="space-y-4">
          {activeCalls.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <PhoneIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No active calls right now</p>
            </div>
          ) : (
            activeCalls.map((call) => (
              <div key={call.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <PhoneIcon className="w-5 h-5 text-green-600 animate-pulse" />
                      <h3 className="text-lg font-semibold text-gray-900">{call.phone_number}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[call.status] || 'bg-gray-100 text-gray-700'}`}>
                        {call.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <div className="text-sm text-gray-500">Duration</div>
                        <div className="text-lg font-medium text-gray-900">
                          {formatDuration(call.duration || 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Sentiment</div>
                        <div className="flex items-center gap-2">
                          {call.sentiment_score && call.sentiment_score > 0.3 ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-600" />
                          ) : call.sentiment_score && call.sentiment_score < -0.3 ? (
                            <XCircleIcon className="w-5 h-5 text-red-600" />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                          <span className="text-sm font-medium">
                            {call.sentiment_score ? call.sentiment_score.toFixed(2) : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {call.transcript && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-500 mb-1">Live Transcript</div>
                        <div className="text-sm text-gray-700">{call.transcript}</div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => handleHangup(call.call_sid!)}
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                      disabled={!call.call_sid}
                    >
                      End Call
                    </button>
                    <button
                      onClick={() => {
                        const toNumber = prompt('Transfer to number:');
                        if (toNumber && call.call_sid) handleTransfer(call.call_sid, toNumber);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                      disabled={!call.call_sid}
                    >
                      Transfer
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Call History */}
      {tab === 'completed' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Outcome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {calls.map((call) => (
                <tr key={call.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {call.phone_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[call.status] || 'bg-gray-100 text-gray-700'}`}>
                      {call.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {call.outcome ? (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${outcomeColors[call.outcome] || 'bg-gray-100 text-gray-700'}`}>
                        {call.outcome.replace('_', ' ')}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {call.duration ? formatDuration(call.duration) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {call.lead_score ? (
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">{call.lead_score}</span>
                        <span className="text-xs text-gray-500 ml-1">/100</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {call.start_time ? format(new Date(call.start_time), 'MMM d, h:mm a') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => navigate(`/crm/telemarketing/calls/${call.id}`)}
                      className="text-purple-600 hover:text-purple-900"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {calls.length === 0 && (
            <div className="p-8 text-center">
              <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No completed calls yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
